import AddRounded from '@mui/icons-material/AddRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import type { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useState } from 'react';

import type {
  ClientCategoriesStatus,
  ClientCategory,
  ClientCategoryAddState,
} from '../domain/licence-client-categories';
import { LicenceFieldLabel } from './licence-field';
import { licenceFieldSx } from './licence-field.styles';

export interface ClientCategoryFieldProps {
  id: string;
  /** The picked category's key; `''` until one is picked. */
  value: string;
  /** The categories to offer (`clientCategoryOptions`: the list, plus the licence's own). */
  options: readonly ClientCategory[];
  status: ClientCategoriesStatus;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  /**
   * Given to an Administrator only (#604): offers "Add a category" at the end of
   * the list. The form picks the category once it is created.
   */
  onAdd?: (label: string) => void;
  /** Where the add request stands; required with `onAdd`. */
  add?: ClientCategoryAddState;
  /** The input was edited or cancelled: drop a refusal about the previous text. */
  onAddCleared?: () => void;
}

/** A value no category can have (keys are `[a-z0-9_]`), for the "Add a category" entry. */
const ADD_ENTRY = '+add';

const HELPER: Record<ClientCategoriesStatus, string | undefined> = {
  idle: undefined,
  loading: 'Loading the client categories…',
  loaded: undefined,
  failed: "The client categories couldn't be loaded.",
};

// The input keeps the dialog's full width, like the other fields; its buttons sit
// on a line of their own under it, right-aligned like the dialog's actions.
const addButtonsSx: SxProps<Theme> = { display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 };
const addEntrySx: SxProps<Theme> = { gap: 1, color: 'primary.main', fontWeight: 600 };

/**
 * "Client category": a select over the list the forms load from the database
 * (#604). For an Administrator (`onAdd`), its last entry, "Add a category",
 * turns the select into a text input with Add / Cancel: the category is created,
 * then picked, without leaving the form.
 *
 * Enter in the input adds rather than submitting the dialog's form, and Escape
 * goes back to the select rather than closing the dialog.
 */
export default function ClientCategoryField({
  id,
  value,
  options,
  status,
  onChange,
  error,
  disabled,
  onAdd,
  add,
  onAddCleared,
}: ClientCategoryFieldProps) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');

  // Back to the select once the category is created; the form has picked it.
  const added = add?.added ?? null;
  const [seenAdded, setSeenAdded] = useState(added);
  if (added !== seenAdded) {
    setSeenAdded(added);
    if (added) {
      setAdding(false);
      setLabel('');
    }
  }

  const pending = add?.status === 'pending';

  const submitAdd = () => {
    const trimmed = label.trim();
    if (trimmed && !pending) onAdd?.(trimmed);
  };
  const cancelAdd = () => {
    setAdding(false);
    setLabel('');
    onAddCleared?.();
  };

  if (adding && onAdd) {
    const inputId = `${id}-new`;
    return (
      <div>
        <LicenceFieldLabel htmlFor={inputId}>Client category</LicenceFieldLabel>
        <TextField
          id={inputId}
          autoFocus
          fullWidth
          hiddenLabel
          variant="filled"
          placeholder="Name of the new category"
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
            if (add?.error) onAddCleared?.();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitAdd();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              cancelAdd();
            }
          }}
          error={Boolean(add?.error)}
          helperText={add?.error ?? 'It is added to the list for every licence.'}
          disabled={disabled || pending}
          slotProps={{ input: { disableUnderline: true } }}
          sx={licenceFieldSx}
        />
        <Box sx={addButtonsSx}>
          {/* Named in full: the dialog has a Cancel of its own. */}
          <Button
            onClick={cancelAdd}
            color="inherit"
            disabled={pending}
            aria-label="Cancel adding a category"
          >
            Cancel
          </Button>
          <Button
            variant="soft"
            color="primary"
            onClick={submitAdd}
            aria-label="Add the category"
            disabled={disabled || pending || !label.trim()}
            startIcon={pending ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Add
          </Button>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <LicenceFieldLabel id={`${id}-label`}>Client category</LicenceFieldLabel>
      <TextField
        id={id}
        select
        fullWidth
        required
        hiddenLabel
        variant="filled"
        value={value}
        onChange={(e) => {
          if (e.target.value === ADD_ENTRY) setAdding(true);
          else onChange(e.target.value);
        }}
        error={Boolean(error)}
        helperText={error ?? HELPER[status]}
        disabled={disabled}
        slotProps={{
          input: { disableUnderline: true },
          select: {
            displayEmpty: true,
            // The visible label names the combobox; `hiddenLabel` leaves none.
            SelectDisplayProps: { 'aria-labelledby': `${id}-label` },
            renderValue: (selected) =>
              options.find((c) => c.value === selected)?.label ?? 'Choose a category',
          },
        }}
        sx={(t) => ({
          ...licenceFieldSx(t),
          // The placeholder reads as one, not as a chosen value.
          '& .MuiSelect-select': { color: value ? 'text.primary' : 'text.disabled' },
        })}
      >
        {options.map((category) => (
          <MenuItem key={category.value} value={category.value}>
            {category.label}
          </MenuItem>
        ))}
        {onAdd && options.length > 0 && <Divider />}
        {onAdd && (
          <MenuItem value={ADD_ENTRY} sx={addEntrySx}>
            <AddRounded fontSize="small" />
            Add a category
          </MenuItem>
        )}
      </TextField>
    </div>
  );
}
